import { generateClient, post } from "aws-amplify/api";
import { createBillingEventLog } from "../graphql/mutations";
import {
  billingEventLogsByUser,
  getPlan as getPlanQuery,
  getUser as getUserQuery,
  listPlans as listPlansQuery,
} from "../graphql/queries";
import { fetchPublicJson } from "./publicContent";
import { graphqlUserPool } from "./graphqlUserPool";

export type PlanStatus =
  | "ACTIVE"
  | "PAYMENT_PENDING"
  | "ACTION_REQUIRED"
  | "PAYMENT_FAILED"
  | "CANCELED";

export type BillingEventType =
  | "PLAN_SELECTED"
  | "PLAN_UPGRADED"
  | "PLAN_DOWNGRADED"
  | "CHECKOUT_STARTED"
  | "CHECKOUT_RESUMED"
  | "CHECKOUT_PAY_LATER"
  | "PAYMENT_SUCCESS"
  | "PAYMENT_FAILED"
  | "SUBSCRIPTION_ACTIVATED"
  | "ACTIVATION_PENDING"
  | "PLAN_REMOVED"
  | "SUBSCRIPTION_CANCELED";

export type BillingPlan = {
  id?: string;
  planId?: string;
  name?: string | null;
  description?: string | null;
  type?: string | null;
  price?: number | null;
  billingCycle?: string | null;
  status?: string | null;
  messagesAllowed?: number | null;
  attachmentsAllowed?: number | null;
  recipientsAllowed?: number | null;
  billingProviderId?: string | null;
  checkoutMode?: string | null;
  priceId?: string | null;
  billingConfig?: Record<string, unknown> | null;
};

export type BillingUserProfile = {
  firstName?: string | null;
  lastName?: string | null;
  preferredName?: string | null;
  primaryPhone?: string | null;
  isActive?: boolean | null;
};

export type BillingUser = {
  id: string;
  email?: string | null;
  UserProfile?: BillingUserProfile | null;
  billingProviderId?: string | null;
  // Active plan identity. Always a real Plan.id, including the free plan.
  userPlanId?: string | null;
  // Immediate checkout-driven change in progress only.
  pendingPlanId?: string | null;
  // Future downgrade to apply at currentPeriodEnd only.
  scheduledPlanId?: string | null;
  // Lifecycle state only. Never encodes plan identity.
  planStatus?: PlanStatus | null;
  checkoutSessionRef?: string | null;
  checkoutMode?: string | null;
  pendingPaymentAmount?: number | null;
  pendingPaymentQuotedAt?: string | null;
  latestPaymentId?: string | null;
  currentPeriodEnd?: string | null;
  billingProvider?: string | null;
  billingCustomerRef?: string | null;
  subscriptionId?: string | null;
};

export type BillingPlanActionKind =
  | "checkout"
  | "scheduled_downgrade"
  | "invalid";

export type BillingPlanChangeEvaluation = {
  kind: BillingPlanActionKind;
  alreadyScheduled?: boolean;
};

export type BillingCheckoutSessionResult =
  | {
      action: "checkout";
      checkoutSessionRef: string;
      checkoutUrl: string;
      billingProvider?: string;
      billingProviderId?: string;
    }
  | {
      action: "defer_checkout";
      pendingPlanId: string;
      pendingPaymentAmount?: number;
      billingProvider?: string;
      billingProviderId?: string;
    }
  | {
      action: "clear_pending_checkout";
      userPlanId?: string;
    }
  | {
      action: "cancel_plan_change";
      userPlanId?: string;
    }
  | {
      action: "scheduled_downgrade";
      scheduledPlanId: string;
      effectiveAt: string;
      billingProvider?: string;
      billingProviderId?: string;
    };

export type BillingCheckoutPreviewResult = {
  action: "preview_checkout";
  activationFlow?: string;
  currentPlanId?: string | null;
  targetPlanId: string;
  estimatedPendingAmount: number | null;
  upgradeCreditAmount: number | null;
  upgradeFeeAmount: number | null;
  currency: string | null;
  requiresImmediatePayment: boolean;
};

export type BillingLifecycleState = {
  normalizedStatus: PlanStatus;
  hasPendingCheckout: boolean;
  hasScheduledDowngrade: boolean;
  hasPaymentFailure: boolean;
  isCanceledAtPeriodEnd: boolean;
  shouldRedirectToCheckout: boolean;
  canResumeCheckout: boolean;
  billingEffectiveAt: string | null;
};

export type BillingPaymentSummary = {
  paymentDate: string | null;
  amount: number | null;
  currency: string | null;
};

export const SELECTED_PLAN_STORAGE_KEY = "mtam.selectedPlanId";
export const SKIP_CHECKOUT_UNTIL_NEXT_LOGIN_KEY =
  "mtam.skipCheckoutUntilNextLogin";

const BILLING_PROVIDER_API_BASE = (
  process.env.REACT_APP_BILLING_PROVIDER_API_BASE_URL ?? ""
)
  .trim()
  .replace(/\/+$/, "");

const withId = <T extends { id?: string; planId?: string }>(
  plan: T,
): T & { planId: string } => {
  const planId = (plan.planId ?? plan.id ?? "").trim();
  return { ...plan, planId };
};

const normalizeBillingConfig = (
  billingConfig: BillingPlan["billingConfig"] | string | null | undefined,
): Record<string, unknown> | null => {
  if (!billingConfig) return null;
  if (typeof billingConfig === "object") {
    return billingConfig as Record<string, unknown>;
  }

  try {
    const parsed = JSON.parse(billingConfig);
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

const normalizeBillingPlan = (
  plan: (BillingPlan & { billingConfig?: BillingPlan["billingConfig"] | string }) | null | undefined,
): (BillingPlan & { planId: string }) | null => {
  if (!plan) return null;

  return withId({
    ...plan,
    billingConfig: normalizeBillingConfig(plan.billingConfig),
  });
};

const resolveSessionRef = (payload: Record<string, any>): string => {
  return String(
    payload.checkoutSessionRef ??
      payload.checkoutSessionId ??
      payload.sessionRef ??
      payload.sessionId ??
      "",
  ).trim();
};

const resolveCheckoutUrl = (payload: Record<string, any>): string => {
  return String(payload.checkoutUrl ?? payload.url ?? "").trim();
};

const requestBillingCheckoutPayload = async (input: {
  planId: string;
  action:
    | "checkout"
    | "preview_checkout"
    | "defer_checkout"
    | "clear_pending_checkout"
    | "cancel_plan_change";
}): Promise<Record<string, any>> => {
  if (!BILLING_PROVIDER_API_BASE) {
    throw new Error("Billing provider API base URL is not configured.");
  }

  const operation = post({
    apiName: "billingProviderApi",
    path: "/checkout/session",
    options: {
      body: {
        action: input.action,
        planId: input.planId,
      },
      headers: {
        "Content-Type": "application/json",
      },
      defaultAuthMode: "iam",
    },
  });

  const resp = await operation.response;

  let payload: Record<string, any>;
  try {
    payload = (await resp.body.json()) as Record<string, any>;
  } catch {
    payload = {};
  }

  if (resp.statusCode < 200 || resp.statusCode >= 300) {
    const message = String(
      payload.message ??
        payload.error ??
        `Checkout session failed (${resp.statusCode})`,
    );
    throw new Error(message);
  }

  return payload;
};

export const isActivePlan = (plan?: BillingPlan | null): boolean =>
  (plan?.status ?? "").toLowerCase() === "active";

export const isPaidPlan = (plan?: BillingPlan | null): boolean => {
  if (!plan) return false;
  const price = Number(plan.price ?? 0);
  const cycle = (plan.billingCycle ?? "").trim().toLowerCase();
  const type = (plan.type ?? "").trim().toLowerCase();
  return price > 0 && cycle !== "no billing" && type !== "free";
};

export function isFreePlan(plan?: BillingPlan | null): boolean {
  if (!plan) return false;

  const type = String(plan.type ?? "")
    .trim()
    .toLowerCase();
  const cycle = String(plan.billingCycle ?? "")
    .trim()
    .toLowerCase();
  const price = Number(plan.price ?? 0);

  return type === "free" || (price === 0 && cycle === "no billing");
}

/**
 * A one-time payment for permanent access: a priced plan with no billing cycle. Decided by the
 * billing attributes only (the same split billingIntegrationFunction uses for "payment" vs
 * "subscription" checkout) - never by `type`, which a lifetime tier shares with its recurring
 * counterpart (production: "Ultimate Lifetime" and "Ultimate" are both type "Ultimate").
 */
export function isLifetimePaidPlan(plan?: BillingPlan | null): boolean {
  if (!plan) return false;
  const type = String((plan as any).type ?? "")
    .trim()
    .toLowerCase();
  const cycle = String((plan as any).billingCycle ?? "")
    .trim()
    .toLowerCase();
  const price = Number((plan as any).price ?? 0);
  return type !== "free" && cycle === "no billing" && price > 0;
}

export function isRecurringPaidPlan(plan?: BillingPlan | null): boolean {
  return isPaidPlan(plan) && !isLifetimePaidPlan(plan);
}

/**
 * True when `targetPlan` must be shown as unavailable because the user's current plan is a
 * lifetime plan: every other non-lifetime plan is off limits. Another lifetime plan is NOT
 * reported here - moving between lifetime tiers is a separate (future) flow, and /Checkout's
 * lifetime restriction still blocks it today.
 */
export function isPlanUnavailableForLifetimeUser(
  currentPlan?: BillingPlan | null,
  targetPlan?: BillingPlan | null,
): boolean {
  if (!isLifetimePaidPlan(currentPlan)) return false;

  const targetPlanId = (targetPlan?.planId ?? targetPlan?.id ?? "").trim();
  const currentPlanId = (currentPlan?.planId ?? currentPlan?.id ?? "").trim();
  if (!targetPlanId || targetPlanId === currentPlanId) return false;

  return !isLifetimePaidPlan(targetPlan);
}

export function evaluateBillingPlanChange(input: {
  user?: BillingUser | null;
  currentPlan?: BillingPlan | null;
  targetPlan?: BillingPlan | null;
}): BillingPlanChangeEvaluation {
  const user = input.user ?? null;
  const currentPlan = input.currentPlan ?? null;
  const targetPlan = input.targetPlan ?? null;

  const targetPlanId = (targetPlan?.planId ?? targetPlan?.id ?? "").trim();
  const currentPlanId = (currentPlan?.planId ?? currentPlan?.id ?? "").trim();
  const scheduledPlanId = (user?.scheduledPlanId ?? "").trim();

  if (!targetPlanId || !isActivePlan(targetPlan)) {
    return { kind: "invalid" };
  }

  if (targetPlanId === currentPlanId) {
    return { kind: "invalid" };
  }

  if (!currentPlan || isFreePlan(currentPlan)) {
    return isPaidPlan(targetPlan) ? { kind: "checkout" } : { kind: "invalid" };
  }

  if (!targetPlan) {
    return { kind: "invalid" };
  }

  const currentPrice = Number(currentPlan.price ?? 0);
  const targetPrice = Number(targetPlan.price ?? 0);

  if (!isRecurringPaidPlan(currentPlan)) {
    return isPaidPlan(targetPlan) ? { kind: "checkout" } : { kind: "invalid" };
  }

  if (!(user?.currentPeriodEnd ?? "").trim() || !(user?.subscriptionId ?? "").trim()) {
    return { kind: "invalid" };
  }

  if (isFreePlan(targetPlan)) {
    return {
      kind: "scheduled_downgrade",
      alreadyScheduled: scheduledPlanId === targetPlanId,
    };
  }

  if (!isPaidPlan(targetPlan)) {
    return { kind: "invalid" };
  }

  if (targetPrice > currentPrice) {
    return { kind: "checkout" };
  }

  if (targetPrice < currentPrice && isRecurringPaidPlan(targetPlan)) {
    return {
      kind: "scheduled_downgrade",
      alreadyScheduled: scheduledPlanId === targetPlanId,
    };
  }

  return { kind: "invalid" };
}

export function getBillingLifecycleState(
  user?: BillingUser | null,
): BillingLifecycleState {
  const normalizedStatus =
    ((user?.planStatus ?? "ACTIVE").toUpperCase() as PlanStatus) || "ACTIVE";
  const pendingPlanId = (user?.pendingPlanId ?? "").trim();
  const scheduledPlanId = (user?.scheduledPlanId ?? "").trim();
  const currentPeriodEnd = (user?.currentPeriodEnd ?? "").trim();
  const hasPendingCheckout =
    !!pendingPlanId &&
    (normalizedStatus === "PAYMENT_PENDING" ||
      normalizedStatus === "ACTION_REQUIRED" ||
      normalizedStatus === "PAYMENT_FAILED");

  return {
    normalizedStatus,
    hasPendingCheckout,
    hasScheduledDowngrade: !!scheduledPlanId && !!currentPeriodEnd,
    hasPaymentFailure: normalizedStatus === "PAYMENT_FAILED",
    isCanceledAtPeriodEnd:
      normalizedStatus === "CANCELED" && !!currentPeriodEnd,
    shouldRedirectToCheckout: hasPendingCheckout,
    canResumeCheckout: hasPendingCheckout,
    billingEffectiveAt: currentPeriodEnd || null,
  };
}

export function shouldRedirectToCheckout(user?: BillingUser | null): boolean {
  return getBillingLifecycleState(user).shouldRedirectToCheckout;
}

export function canResumeCheckout(user?: BillingUser | null): boolean {
  return getBillingLifecycleState(user).canResumeCheckout;
}

export function hasScheduledDowngrade(user?: BillingUser | null): boolean {
  return getBillingLifecycleState(user).hasScheduledDowngrade;
}

export function getBillingPlanTypeLabelKey(
  plan?: BillingPlan | null,
): string {
  if (isFreePlan(plan)) return "profile.planTypeFree";
  if (isLifetimePaidPlan(plan)) return "profile.planTypeLifetime";
  if (isRecurringPaidPlan(plan)) return "profile.planTypeRecurringPaid";
  if (isPaidPlan(plan)) return "profile.planTypeOneTimePaid";
  return "profile.planTypeUnknown";
}

export function getBillingTimelineLabelKey(input: {
  user?: BillingUser | null;
  plan?: BillingPlan | null;
}): string | null {
  const user = input.user ?? null;
  const plan = input.plan ?? null;
  const currentPeriodEnd = (user?.currentPeriodEnd ?? "").trim();

  if (!currentPeriodEnd) {
    return null;
  }

  const billingState = getBillingLifecycleState(user);
  if (
    billingState.hasScheduledDowngrade ||
    billingState.hasPaymentFailure ||
    billingState.isCanceledAtPeriodEnd
  ) {
    return "profile.accessValidUntil";
  }

  if (isRecurringPaidPlan(plan)) {
    return "profile.nextBillingDate";
  }

  return "profile.accessValidUntil";
}

export function formatBillingProviderLabel(
  provider?: string | null,
): string | null {
  const normalizedProvider = (provider ?? "").trim();
  if (!normalizedProvider) return null;

  const upperProvider = normalizedProvider.toUpperCase();
  if (upperProvider === "STRIPE") {
    return "Stripe";
  }

  return (
    normalizedProvider.charAt(0).toUpperCase() +
    normalizedProvider.slice(1).toLowerCase()
  );
}

export const needsCheckout = (user?: BillingUser | null): boolean => {
  return shouldRedirectToCheckout(user);
};

// Presentation-only plan catalog from plans.json. Never use for billing logic.
export async function getPlans(
  signal?: AbortSignal,
): Promise<Array<BillingPlan & { planId: string }>> {
  const plans = await fetchPublicJson<BillingPlan[]>("plans.json", signal);
  if (!Array.isArray(plans)) return [];
  return plans.map(withId).filter((plan) => !!plan.planId);
}

export async function getBillingPlan(
  planId: string,
): Promise<(BillingPlan & { planId: string }) | null> {
  const normalizedPlanId = (planId ?? "").trim();
  if (!normalizedPlanId) return null;

  const data: any = await graphqlUserPool({
    query: getPlanQuery as any,
    variables: { id: normalizedPlanId },
  });

  return normalizeBillingPlan(data?.getPlan ?? null);
}

export async function getScheduledBillingPlan(
  user?: BillingUser | null,
): Promise<(BillingPlan & { planId: string }) | null> {
  const scheduledPlanId = (user?.scheduledPlanId ?? "").trim();
  if (!scheduledPlanId) return null;
  return getBillingPlan(scheduledPlanId);
}

export async function getLatestSuccessfulBillingPayment(
  userId: string,
): Promise<BillingPaymentSummary | null> {
  const normalizedUserId = (userId ?? "").trim();
  if (!normalizedUserId) return null;

  const client = generateClient();
  const result: any = await client.graphql({
    query: billingEventLogsByUser as any,
    variables: {
      userId: normalizedUserId,
      sortDirection: "DESC",
      limit: 25,
    },
  });

  const items = Array.isArray(result?.data?.billingEventLogsByUser?.items)
    ? result.data.billingEventLogsByUser.items.filter(Boolean)
    : [];

  const getEventStatus = (event: Record<string, unknown>) =>
    String(event.status ?? "")
      .trim()
      .toUpperCase();
  const getEventType = (event: Record<string, unknown>) =>
    String(event.eventType ?? "")
      .trim()
      .toUpperCase();

  const successfulPaymentEvent =
    items.find(
      (event: Record<string, unknown>) =>
        getEventType(event) === "PAYMENT_SUCCESS" &&
        getEventStatus(event) === "SUCCEEDED",
    ) ??
    items.find(
      (event: Record<string, unknown>) =>
        getEventType(event) === "PAYMENT_SUCCESS",
    );
  const successfulActivationEvent = items.find(
    (event: Record<string, unknown>) =>
      (getEventType(event) === "SUBSCRIPTION_ACTIVATED" ||
        getEventType(event) === "PLAN_UPGRADED") &&
      getEventStatus(event) === "SUCCEEDED",
  );
  const matchingEvent =
    successfulPaymentEvent ??
    successfulActivationEvent ??
    items.find(
      (event: Record<string, unknown>) =>
        getEventType(event) === "SUBSCRIPTION_ACTIVATED" ||
        getEventType(event) === "PLAN_UPGRADED",
    );

  if (!matchingEvent) {
    return null;
  }

  return {
    paymentDate:
      String(
        matchingEvent.occurredAt ??
          matchingEvent.processedAt ??
          matchingEvent.receivedAt ??
          "",
      ).trim() || null,
    amount:
      typeof matchingEvent.amount === "number" &&
      Number.isFinite(matchingEvent.amount)
        ? matchingEvent.amount
        : null,
    currency:
      typeof matchingEvent.currency === "string"
        ? matchingEvent.currency.trim() || null
        : null,
  };
}

export async function listBillingPlans(): Promise<
  Array<BillingPlan & { planId: string }>
> {
  const plans: Array<BillingPlan & { planId: string }> = [];
  let nextToken: string | null | undefined = null;

  do {
    const data: any = await graphqlUserPool({
      query: listPlansQuery as any,
      variables: {
        limit: 1000,
        nextToken,
      },
    });

    const page = data?.listPlans;
    const items = Array.isArray(page?.items) ? page.items : [];

    plans.push(
      ...items
        .map((plan: BillingPlan) => normalizeBillingPlan(plan))
        .filter(
          (plan: BillingPlan & { planId: string } | null): plan is BillingPlan & { planId: string } =>
            !!plan && !!plan.planId,
        ),
    );

    nextToken = page?.nextToken ?? null;
  } while (nextToken);

  return plans;
}

export function getFreePlanId(plans: BillingPlan[]): string | null {
  if (!Array.isArray(plans) || plans.length === 0) return null;

  const free = plans.find((p) => {
    if (!isActivePlan(p)) return false;

    const type = String((p as any).type ?? "")
      .trim()
      .toLowerCase();
    if (type === "free") return true;

    const price = Number((p as any).price ?? 0);
    const cycle = String((p as any).billingCycle ?? "")
      .trim()
      .toLowerCase();

    return price === 0 && cycle === "no billing";
  });

  return free ? (free.planId ?? free.id ?? "").trim() || null : null;
}

export function computeNextPeriodEnd(
  billingCycle?: string | null,
): string | null {
  const cycle = (billingCycle ?? "").trim().toLowerCase();
  const next = new Date();

  if (cycle.includes("year")) {
    next.setFullYear(next.getFullYear() + 1);
    return next.toISOString();
  }

  if (cycle.includes("month")) {
    next.setMonth(next.getMonth() + 1);
    return next.toISOString();
  }

  return null;
}

export async function getUser(userId: string): Promise<BillingUser | null> {
  const client = generateClient();
  const result: any = await client.graphql({
    query: getUserQuery as any,
    variables: { id: userId },
  });
  return (result?.data?.getUser ?? null) as BillingUser | null;
}

export async function createBillingCheckoutSession(input: {
  planId: string;
  action?:
    | "checkout"
    | "defer_checkout"
    | "clear_pending_checkout"
    | "cancel_plan_change";
}): Promise<BillingCheckoutSessionResult> {
  const payload = await requestBillingCheckoutPayload({
    planId: input.planId,
    action: input.action ?? "checkout",
  });

  const action =
    typeof payload.action === "string"
      ? payload.action.trim().toLowerCase()
      : "checkout";

  if (action === "scheduled_downgrade") {
    const scheduledPlanId =
      typeof payload.scheduledPlanId === "string"
        ? payload.scheduledPlanId.trim()
        : "";
    const effectiveAt =
      typeof payload.effectiveAt === "string"
        ? payload.effectiveAt.trim()
        : "";

    if (!scheduledPlanId || !effectiveAt) {
      throw new Error(
        "Billing provider did not return a valid scheduled downgrade response.",
      );
    }

    return {
      action: "scheduled_downgrade",
      scheduledPlanId,
      effectiveAt,
      billingProvider:
        typeof payload.billingProvider === "string"
          ? payload.billingProvider
          : undefined,
      billingProviderId:
        typeof payload.billingProviderId === "string"
          ? payload.billingProviderId
          : undefined,
    };
  }

  if (action === "defer_checkout") {
    const pendingPlanId =
      typeof payload.pendingPlanId === "string"
        ? payload.pendingPlanId.trim()
        : "";

    if (!pendingPlanId) {
      throw new Error(
        "Billing provider did not return a valid deferred checkout response.",
      );
    }

    return {
      action: "defer_checkout",
      pendingPlanId,
      pendingPaymentAmount:
        typeof payload.pendingPaymentAmount === "number" &&
        Number.isFinite(payload.pendingPaymentAmount)
          ? payload.pendingPaymentAmount
          : undefined,
      billingProvider:
        typeof payload.billingProvider === "string"
          ? payload.billingProvider
          : undefined,
      billingProviderId:
        typeof payload.billingProviderId === "string"
          ? payload.billingProviderId
          : undefined,
    };
  }

  if (action === "clear_pending_checkout") {
    return {
      action: "clear_pending_checkout",
      userPlanId:
        typeof payload.userPlanId === "string"
          ? payload.userPlanId
          : undefined,
    };
  }

  if (action === "cancel_plan_change") {
    return {
      action: "cancel_plan_change",
      userPlanId:
        typeof payload.userPlanId === "string"
          ? payload.userPlanId
          : undefined,
    };
  }

  const checkoutSessionRef = resolveSessionRef(payload);
  const checkoutUrl = resolveCheckoutUrl(payload);

  if (!checkoutSessionRef || !checkoutUrl) {
    throw new Error(
      "Billing provider did not return a valid checkout session.",
    );
  }

  return {
    action: "checkout",
    checkoutSessionRef,
    checkoutUrl,
    billingProvider:
      typeof payload.billingProvider === "string"
        ? payload.billingProvider
        : undefined,
    billingProviderId:
      typeof payload.billingProviderId === "string"
        ? payload.billingProviderId
        : undefined,
  };
}

export async function previewBillingCheckout(input: {
  planId: string;
}): Promise<BillingCheckoutPreviewResult> {
  const payload = await requestBillingCheckoutPayload({
    planId: input.planId,
    action: "preview_checkout",
  });

  const targetPlanId =
    typeof payload.targetPlanId === "string" ? payload.targetPlanId.trim() : "";

  if (!targetPlanId) {
    throw new Error("Billing provider did not return a valid checkout preview.");
  }

  return {
    action: "preview_checkout",
    activationFlow:
      typeof payload.activationFlow === "string"
        ? payload.activationFlow
        : undefined,
    currentPlanId:
      typeof payload.currentPlanId === "string" ? payload.currentPlanId : null,
    targetPlanId,
    estimatedPendingAmount:
      typeof payload.estimatedPendingAmount === "number" &&
      Number.isFinite(payload.estimatedPendingAmount)
        ? payload.estimatedPendingAmount
        : null,
    upgradeCreditAmount:
      typeof payload.upgradeCreditAmount === "number" &&
      Number.isFinite(payload.upgradeCreditAmount)
        ? payload.upgradeCreditAmount
        : null,
    upgradeFeeAmount:
      typeof payload.upgradeFeeAmount === "number" &&
      Number.isFinite(payload.upgradeFeeAmount)
        ? payload.upgradeFeeAmount
        : null,
    currency:
      typeof payload.currency === "string" && payload.currency.trim()
        ? payload.currency
        : null,
    requiresImmediatePayment: payload.requiresImmediatePayment !== false,
  };
}

export async function logBillingEvent(input: {
  userId: string;
  eventType: BillingEventType;
  metadata?: Record<string, unknown>;
  status?: string;
  provider?: string;
  providerEventId?: string;
  providerCustomerRef?: string;
  providerResourceRef?: string;
  amount?: number;
  currency?: string;
  message?: string;
}): Promise<void> {
  const client = generateClient();
  const timestamp = new Date().toISOString();
  const providerEventId =
    input.providerEventId ??
    `evt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const metadata = input.metadata ?? {};
  const planIdValue = metadata.planId;
  const planId = typeof planIdValue === "string" ? planIdValue : undefined;
  const sessionRefValue = metadata.sessionRef;
  const sessionRef =
    typeof sessionRefValue === "string" ? sessionRefValue : undefined;

  await client.graphql({
    query: createBillingEventLog,
    variables: {
      input: {
        userId: input.userId,
        provider: input.provider ?? "app",
        providerEventId,
        providerCustomerRef: input.providerCustomerRef,
        providerResourceRef: input.providerResourceRef ?? sessionRef,
        eventType: input.eventType,
        status: input.status ?? "INFO",
        planId,
        amount: input.amount,
        currency: input.currency,
        message: input.message,
        occurredAt: timestamp,
        receivedAt: timestamp,
        processedAt: timestamp,
        rawPayload: JSON.stringify({ ...metadata, timestamp }),
      },
    },
  } as any);
}

